import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { equipmentService, categoryService, branchService } from '../../services/api';
import { Card, Input, Select, Button, useToast } from '../../components/common';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import { usePageTitle } from '../../context/PageTitleContext';

// Add / edit / view one piece of equipment. Follows CreateEditBranch so the
// form behaves like every other master: Card + Breadcrumbs + shared Input and
// Select, with the same view/edit/create mode handling.
const CreateEditEquipment = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();

  const isViewMode = location.pathname.includes('/view/');
  const isEditMode = location.pathname.includes('/edit/');

  const [form, setForm] = useState({
    code: '',
    name: '',
    category_code: '',
    branch_id: '',
    manufacturer: '',
    model: '',
    serial_number: ''
  });
  const [regionName, setRegionName] = useState('');
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(Boolean(id));
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    setPageTitle(isViewMode ? 'View Equipment' : isEditMode ? 'Edit Equipment' : 'Add Equipment');
  }, [setPageTitle, isViewMode, isEditMode]);

  // Load the record first, then the dropdowns — the category it already holds
  // is passed as include_codes so a category that has since been switched off
  // is still offered. Without that, opening the record would silently clear
  // the field on save.
  useEffect(() => {
    let currentCategory = '';

    const loadRecord = async () => {
      if (!id) return;
      const res = await equipmentService.getById(id);
      const e = res.data;
      currentCategory = e.category_code || '';
      setForm({
        code: e.code || '',
        name: e.name || '',
        category_code: currentCategory,
        branch_id: e.branch_id || '',
        manufacturer: e.manufacturer || '',
        model: e.model || '',
        serial_number: e.serial_number || ''
      });
      setRegionName(e.region_name || '');
    };

    (async () => {
      try {
        setFetching(Boolean(id));
        await loadRecord();

        const params = { dropdown: true };
        if (currentCategory) params.include_codes = currentCategory;

        const [cat, br] = await Promise.all([
          categoryService.getAll(params),
          branchService.getAll({ noPagination: 'true' })
        ]);
        setCategories(Array.isArray(cat.data) ? cat.data : cat.data.data || []);
        setBranches(br.data.data || []);
      } catch {
        setError(id ? 'Could not load this equipment' : 'Could not load categories or branches');
      } finally {
        setFetching(false);
      }
    })();
  }, [id]);

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    if (fieldErrors[key]) setFieldErrors((p) => ({ ...p, [key]: '' }));

    // Picking a branch fills the entity, the same as the BioMedical prototype.
    // The server derives it too — this is just the immediate feedback.
    if (key === 'branch_id') {
      const branch = branches.find((b) => String(b.id) === String(value));
      setRegionName(branch?.region_name || '');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const errors = {};
    if (!form.code.trim()) errors.code = 'Equipment code is required';
    if (!form.name.trim()) errors.name = 'Name of the equipment is required';
    if (!form.category_code) errors.category_code = 'Category is required';
    if (!form.branch_id) errors.branch_id = 'Branch is required';
    if (Object.keys(errors).length) return setFieldErrors(errors);

    setLoading(true);
    try {
      const payload = { ...form, branch_id: form.branch_id || null };
      if (isEditMode) {
        await equipmentService.update(id, payload);
        toast.success('Equipment updated successfully');
      } else {
        await equipmentService.create(payload);
        toast.success('Equipment added successfully');
      }
      navigate('/admin/equipment');
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to save equipment';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="admin-panel">
        <Card>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p>Loading equipment details...</p>
          </div>
        </Card>
      </div>
    );
  }

  // An inactive category only reaches this list because the record already
  // uses it — label it so the user knows why it is still selectable.
  const categoryOptions = categories.map((c) => ({
    value: c.code,
    label: c.status === false ? `${c.name} (inactive)` : c.name
  }));
  const branchOptions = branches.map((b) => ({ value: String(b.id), label: b.name }));

  return (
    <div className="admin-panel">
      <Card className="w-full">
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="w-full">
          <Breadcrumbs
            items={[
              { label: 'Equipment', to: '/admin/equipment' },
              { label: isViewMode ? 'View Equipment' : isEditMode ? 'Edit Equipment' : 'Add Equipment' }
            ]}
            className="mb-6"
          />

          <div className="flex flex-col md:flex-row gap-4 mb-6 w-full">
            <div className="flex-1">
              <Input
                label="Equipment Code"
                name="code"
                value={form.code}
                onChange={set('code')}
                placeholder="EQP-001"
                required
                error={fieldErrors.code}
                maxLength={50}
                disabled={isViewMode}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Name of the equipment"
                name="name"
                value={form.name}
                onChange={set('name')}
                placeholder="Slitlamp"
                required
                error={fieldErrors.name}
                maxLength={255}
                disabled={isViewMode}
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-6 w-full">
            <div className="flex-1">
              <Select
                label="Category"
                value={form.category_code}
                onChange={set('category_code')}
                options={categoryOptions}
                placeholder="Select a category"
                required
                error={fieldErrors.category_code}
                disabled={isViewMode}
              />
            </div>
            <div className="flex-1">
              <Select
                label="Branch"
                value={String(form.branch_id || '')}
                onChange={set('branch_id')}
                options={branchOptions}
                placeholder="Select a branch"
                required
                error={fieldErrors.branch_id}
                disabled={isViewMode}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Entity"
                value={regionName}
                placeholder="Set from the branch"
                readOnly
                disabled
                helperText="Selecting a branch fills the entity automatically."
              />
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-8 w-full">
            <div className="flex-1">
              <Input
                label="Manufacturer"
                value={form.manufacturer}
                onChange={set('manufacturer')}
                placeholder="Appasamy Associates"
                maxLength={255}
                disabled={isViewMode}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Model"
                value={form.model}
                onChange={set('model')}
                placeholder="AIA 11A"
                maxLength={255}
                disabled={isViewMode}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Serial number"
                value={form.serial_number}
                onChange={set('serial_number')}
                placeholder="65010345"
                maxLength={100}
                disabled={isViewMode}
              />
            </div>
          </div>

          {/* Actions Row: Buttons at far right, matching the other masters */}
          <div className="flex w-full justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/equipment')}
              disabled={loading}
              className="border-primary-600 text-primary-600 hover:bg-primary-50"
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </Button>
            {!isViewMode && (
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Saving...' : isEditMode ? 'Update Equipment' : '+ Add Equipment'}
              </Button>
            )}
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateEditEquipment;
